/**
 * 📋 SDLC 6.5.0 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/pages/tenant-admin/BranchGeofenceTab.tsx
 * PURPOSE: SC-028-BRANCH-GEOFENCE — tab "Vị trí chi nhánh" (Điểm vào 2, thứ 5)
 *          trong Cài đặt tổ chức (SC-003-SETTINGS-HUB). Bảng 7 cột tenant-wide,
 *          ⁝ mở modal dùng chung BranchGeofenceModal để Cấu hình/Sửa geofence.
 * SDLC FRAMEWORK VERSION: 6.5.0 (7-Pillar + 9 Mental Models + Long-Running Agent Protocol)
 * DATE: 2026-08-12
 * COMPLIANCE: SDLC 6.5.0 + Zero Mock Tolerance — nối API THẬT /organizations/branches-geofence
 * PILLAR: P1 (Stage 04 - BUILD) + P6 (UI Components)
 * SPRINT GOVERNANCE: Rule 3: Zero-Mock Tolerance | Rule 7: Performance SLAs
 *
 * @business-context
 * - Domain: Tenant Administration, Branch GPS geofence configuration
 * - Impact: Đúng gap ISS-PILOT-6 nêu — Tenant Admin chưa có nơi thao tác geofence
 *   trên UI; tab này liệt kê TOÀN BỘ chi nhánh (node cấp Chi nhánh) của tenant.
 * - Stakeholders: Tenant Admins
 *
 * @technical-architecture
 * - Layer: Presentation Layer (Tab Component của TenantSettings)
 * - Pattern: apiClient (theo UnitTypesTab), inline styles, token var(--nqh-*), toast 4s.
 * - Dependencies: React 18, apiClient, backend/app/api/v1/organization.py (list_branches_geofence)
 *
 * @cross-references
 * - Architecture: docs/02-design/18-adrs/06-decisions/DD-0099-branch-geofence-org-bridge-v0.md
 * - Requirement: docs/01-planning/01-requirements/03-tenant-management/BR-TENM-006-TENANT-SETTINGS.md (FR-012.v0)
 * - Wireframe: docs/02-design/04-ui-ux-design/persona-flows/WIREFRAME-V0-MASTER-REGISTRY.md
 *   §2 SC-028-BRANCH-GEOFENCE (LAYOUT Điểm vào 2, annotation [1]-[5])
 * - Modal: frontend/src/pages/tenant-admin/BranchGeofenceModal.tsx
 * - Host: frontend/src/pages/tenant-admin/TenantSettings.tsx (TAB_CONFIG)
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { apiClient } from "../../services/api/client";
import { PortalMenu, type PortalMenuAnchor } from "../../components/common/PortalMenu";
import { PortalTooltip } from "../../components/common/PortalTooltip";
import { BranchGeofenceModal, type BranchGeofenceTarget } from "./BranchGeofenceModal";

interface BranchGeofenceRow {
  org_id: string;
  name: string;
  code: string;
  latitude: number | null;
  longitude: number | null;
  geofence_radius: number;
  configured: boolean;
}

const st = {
  card: {
    background: "var(--nqh-bg-surface, white)",
    borderRadius: "12px",
    padding: "24px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
  } as React.CSSProperties,
  head: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    marginBottom: "16px",
    flexWrap: "wrap" as const,
  } as React.CSSProperties,
  headTitle: {
    margin: 0,
    fontSize: "16px",
    fontWeight: 600,
    color: "var(--nqh-gray-900, #1f2937)",
  } as React.CSSProperties,
  toolbar: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    marginBottom: "16px",
    flexWrap: "wrap" as const,
  } as React.CSSProperties,
  searchInput: {
    flex: "0 1 280px",
    padding: "8px 12px",
    border: "1px solid var(--nqh-gray-300, #d1d5db)",
    borderRadius: "8px",
    fontSize: "14px",
  } as React.CSSProperties,
  count: {
    marginLeft: "auto",
    fontSize: "14px",
    color: "var(--nqh-gray-500, #6b7280)",
  } as React.CSSProperties,
  tableWrap: {
    overflowX: "auto" as const,
    border: "1px solid var(--nqh-gray-200, #e5e7eb)",
    borderRadius: "12px",
    background: "var(--nqh-bg-surface, white)",
  } as React.CSSProperties,
  table: {
    borderCollapse: "separate" as const,
    borderSpacing: 0,
    width: "100%",
    minWidth: "760px",
    fontSize: "14px",
  } as React.CSSProperties,
  th: {
    background: "var(--nqh-bg-sunken, #f9fafb)",
    color: "var(--nqh-gray-700, #374151)",
    fontSize: "12px",
    fontWeight: 600,
    textTransform: "uppercase" as const,
    letterSpacing: ".02em",
    borderBottom: "1px solid var(--nqh-gray-200, #e5e7eb)",
    padding: "8px 12px",
    textAlign: "left" as const,
    whiteSpace: "nowrap" as const,
  } as React.CSSProperties,
  thRight: {
    textAlign: "right" as const,
  } as React.CSSProperties,
  td: {
    borderBottom: "1px solid var(--nqh-gray-100, #f3f4f6)",
    padding: "8px 12px",
    textAlign: "left" as const,
    verticalAlign: "middle" as const,
    whiteSpace: "nowrap" as const,
  } as React.CSSProperties,
  tdRight: {
    textAlign: "right" as const,
    fontVariantNumeric: "tabular-nums" as const,
  } as React.CSSProperties,
  statusOk: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    fontSize: "12px",
    fontWeight: 600,
    color: "var(--nqh-herb-700, #15803d)",
  } as React.CSSProperties,
  statusOff: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    fontSize: "12px",
    color: "var(--nqh-gray-500, #6b7280)",
  } as React.CSSProperties,
  iconBtn: {
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: "4px 8px",
    color: "var(--nqh-gray-500, #6b7280)",
    fontSize: "16px",
    lineHeight: 1,
    borderRadius: "6px",
  } as React.CSSProperties,
  menuItem: {
    display: "block",
    width: "100%",
    textAlign: "left" as const,
    background: "none",
    border: "none",
    padding: "8px 12px",
    fontSize: "14px",
    color: "var(--nqh-gray-700, #374151)",
    cursor: "pointer",
    borderRadius: "6px",
  } as React.CSSProperties,
  bannerError: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "8px",
    background: "var(--nqh-chili-050, #fef2f2)",
    border: "1px solid var(--nqh-chili-200, #fecaca)",
    borderRadius: "8px",
    padding: "10px 12px",
    marginBottom: "16px",
    color: "var(--nqh-chili, #dc2626)",
    fontSize: "14px",
  } as React.CSSProperties,
  btnSecondary: {
    padding: "8px 16px",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: 500,
    cursor: "pointer",
    border: "none",
    color: "var(--nqh-gray-700, #374151)",
    background: "var(--nqh-gray-200, #e5e7eb)",
  } as React.CSSProperties,
  toast: {
    position: "fixed" as const,
    bottom: "24px",
    left: "50%",
    transform: "translateX(-50%)",
    background: "var(--nqh-gray-900, #1f2937)",
    color: "white",
    padding: "10px 20px",
    borderRadius: "8px",
    fontSize: "14px",
    zIndex: 1100,
    boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
  } as React.CSSProperties,
  skeletonRow: {
    height: "20px",
    borderRadius: "6px",
    background: "var(--nqh-gray-100, #f3f4f6)",
    margin: "12px",
  } as React.CSSProperties,
};

/** D10: chú thích qua icon ⓘ + tooltip microcopy (cột "Trạng thái").
 * 2026-08-25: đổi từ span[title] sang PortalTooltip + SVG — đồng bộ pattern
 * manager (ManagerStaffPage/ManagerAttendancePage/ManagerSchedulePage.tsx). */
const InfoIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} width={14} height={14} aria-hidden="true" style={{ display: "inline-flex", color: "var(--nqh-gray-500)", verticalAlign: "middle", marginLeft: "2px" }}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
  </svg>
);

const fmtCoord = (v: number | null): string => (v === null ? "—" : v.toFixed(4));
const fmtRadius = (row: BranchGeofenceRow): string =>
  row.configured ? `${row.geofence_radius}m` : `${row.geofence_radius} (mặc định)`;

const BranchGeofenceTab: React.FC = () => {
  const [items, setItems] = useState<BranchGeofenceRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<PortalMenuAnchor | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [modalTarget, setModalTarget] = useState<BranchGeofenceTarget | null>(null);

  const menuRef = useRef<HTMLDivElement | null>(null);

  const fetchItems = useCallback(async () => {
    setLoadError(null);
    try {
      const res = await apiClient.get("/organizations/branches-geofence");
      setItems(Array.isArray(res.data) ? res.data : []);
    } catch {
      // ERROR (registry §SC-028)
      setLoadError("Không tải được danh sách vị trí chi nhánh · Thử lại");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  // Toast tự ẩn sau 4s (pattern window.nqhToast trong mockup common.js)
  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(timer);
  }, [toast]);

  // Đóng ⁝ menu khi click ra ngoài
  useEffect(() => {
    if (!openMenuId) return;
    const onDocClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [openMenuId]);

  // [3] Tìm chi nhánh — lọc client-side (API không có tham số tìm kiếm, per DD-0099 §5)
  const filteredItems = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (row) => row.name.toLowerCase().includes(q) || row.code.toLowerCase().includes(q)
    );
  }, [items, searchQuery]);

  const openGeofenceModal = (row: BranchGeofenceRow) => {
    setOpenMenuId(null);
    setModalTarget({ orgId: row.org_id, name: row.name, code: row.code });
  };

  const handleSaved = () => {
    setToast("Đã lưu vị trí chi nhánh");
    fetchItems();
  };

  if (loading) {
    return (
      <div style={st.card} data-testid="branch-geofence-skeleton">
        <div style={st.head}>
          <h3 style={st.headTitle}>Vị trí chi nhánh (Geofence)</h3>
        </div>
        <div style={st.tableWrap}>
          {[0, 1, 2, 3].map((i) => (
            <div key={i} style={st.skeletonRow} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div style={st.card}>
      {/* [2] Tiêu đề — KHÔNG có nút primary cấp trang (registry §SC-028 annotation [2]) */}
      <div style={st.head}>
        <h3 style={st.headTitle}>Vị trí chi nhánh (Geofence)</h3>
      </div>

      {loadError && (
        <div style={st.bannerError} role="alert">
          <span>{loadError}</span>
          <button
            type="button"
            style={st.btnSecondary}
            onClick={() => {
              setLoading(true);
              fetchItems();
            }}
          >
            Thử lại
          </button>
        </div>
      )}

      {!loadError && (
        <>
          {/* [3] Tìm chi nhánh + [4] đếm, đẩy phải */}
          <div style={st.toolbar}>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Tìm chi nhánh…"
              style={st.searchInput}
              aria-label="Tìm chi nhánh"
            />
            <span style={st.count}>{filteredItems.length} chi nhánh</span>
          </div>

          {items.length === 0 ? (
            // EMPTY (registry §SC-028) — KHÔNG có nút Thêm ở màn này
            <p style={{ textAlign: "center", color: "var(--nqh-gray-400, #9ca3af)", padding: "20px" }}>
              Chưa có chi nhánh nào trong cơ cấu tổ chức. Thêm chi nhánh ở Cơ cấu tổ chức trước.
            </p>
          ) : (
            <div style={st.tableWrap}>
              <table style={st.table}>
                <thead>
                  <tr>
                    <th style={st.th} scope="col">Tên chi nhánh</th>
                    <th style={st.th} scope="col">Mã</th>
                    <th style={{ ...st.th, ...st.thRight }} scope="col">Vĩ độ</th>
                    <th style={{ ...st.th, ...st.thRight }} scope="col">Kinh độ</th>
                    <th style={{ ...st.th, ...st.thRight }} scope="col">Bán kính (m)</th>
                    <th style={st.th} scope="col">
                      Trạng thái
                      <PortalTooltip text="Đã cấu hình: chi nhánh đang giới hạn GPS khi chấm công. Chưa cấu hình: chi nhánh không giới hạn GPS." tabIndex={0} ariaLabel="Đã cấu hình: chi nhánh đang giới hạn GPS khi chấm công. Chưa cấu hình: chi nhánh không giới hạn GPS.">
                        <InfoIcon />
                      </PortalTooltip>
                    </th>
                    <th style={{ ...st.th, width: "56px", textAlign: "center" }} scope="col">Thao tác</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredItems.map((row) => (
                    <tr key={row.org_id}>
                      <td style={st.td}>{row.name}</td>
                      <td style={st.td}>{row.code}</td>
                      <td style={{ ...st.td, ...st.tdRight }}>{fmtCoord(row.latitude)}</td>
                      <td style={{ ...st.td, ...st.tdRight }}>{fmtCoord(row.longitude)}</td>
                      <td style={{ ...st.td, ...st.tdRight }}>{fmtRadius(row)}</td>
                      <td style={st.td}>
                        {row.configured ? (
                          <span style={st.statusOk}>● Đã cấu hình</span>
                        ) : (
                          <span style={st.statusOff}>○ Chưa cấu hình</span>
                        )}
                      </td>
                      <td style={{ ...st.td, textAlign: "center" }}>
                        <div
                          style={{ position: "relative", display: "inline-block" }}
                          ref={openMenuId === row.org_id ? menuRef : undefined}
                        >
                          <button
                            type="button"
                            style={st.iconBtn}
                            aria-haspopup="true"
                            aria-expanded={openMenuId === row.org_id}
                            aria-label={`Thao tác — ${row.name}`}
                            onClick={(e) => {
                              if (openMenuId === row.org_id) {
                                setOpenMenuId(null);
                                return;
                              }
                              const r = e.currentTarget.getBoundingClientRect();
                              setMenuAnchor({ top: r.top, bottom: r.bottom, right: window.innerWidth - r.right });
                              setOpenMenuId(row.org_id);
                            }}
                          >
                            ⋮
                          </button>
                          {openMenuId === row.org_id && menuAnchor && (
                            <PortalMenu anchor={menuAnchor} onClose={() => setOpenMenuId(null)} style={{ padding: "4px" }}>
                              <button
                                type="button"
                                style={st.menuItem}
                                role="menuitem"
                                onClick={() => openGeofenceModal(row)}
                              >
                                {row.configured ? "Sửa geofence" : "Cấu hình geofence"}
                              </button>
                            </PortalMenu>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* [6] Modal dùng chung (Điểm vào 2) */}
      <BranchGeofenceModal target={modalTarget} onClose={() => setModalTarget(null)} onSaved={handleSaved} />

      {/* Toast (pattern window.nqhToast) */}
      {toast && (
        <div style={st.toast} role="status">
          {toast}
        </div>
      )}
    </div>
  );
};

export default BranchGeofenceTab;
